/**
 * What a screen says about how current its receivables are
 * (docs/product/phase-0/01-FRESHNESS.md). Plain words, never "live" or
 * "real-time": the newest a figure can be is the last update, and it is always
 * said with its age.
 *
 * Four strengths, one per situation:
 *   quiet    the figures are current — one small line;
 *   notice   slower than usual, being set up, or the age could not be checked;
 *   warning  old figures, or figures nothing is updating any more;
 *   action   something must be done by whoever manages the data source.
 *
 * Who is told what: those who manage or look after the data source
 * (`datasource.manage`, `datasource.view`) are told what is wrong and what to
 * do. Everybody else is told how old the figures are and whether they are
 * still being updated — never which computer, which step or which error.
 */
import { describeAge, type FreshnessProblem } from '@timely/domain';
import type { FreshnessReading } from './useReceivablesFreshness';

export type FreshnessAudience = 'manage' | 'view' | 'member';
export type FreshnessTone = 'quiet' | 'notice' | 'warning' | 'action';

export interface FreshnessMessage {
    tone: FreshnessTone;
    headline: string;
    detail: string | null;
    /** What to do about it, for whoever can. */
    next: string | null;
    /** Offer the way to Settings → Receivables data. */
    linkToSource: boolean;
    /**
     * Said to a screen reader when it changes: the state, without the age, so
     * it is not repeated every minute. Null for a quiet line.
     */
    announce: string | null;
}

export interface AgeFormat {
    day(value: Date | string): string;
    time(value: Date | string): string;
    dayKey(value: Date | string): string;
}

const SOURCE = { tally: 'TallyPrime', google_sheets: 'the Google Sheet' } as const;

const ADMIN_PROBLEM: Record<FreshnessProblem, { headline: string; next: string }> = {
    connector_silent: {
        headline: 'Timely Connector has not checked in',
        next: 'Check that the computer with TallyPrime is switched on and connected to the internet, and that Timely Connector is running on it.',
    },
    tally_not_running: {
        headline: 'TallyPrime is not open on the connected computer',
        next: 'Open TallyPrime on that computer. Timely catches up as soon as it is open.',
    },
    company_not_open: {
        headline: 'The connected company is not open in TallyPrime',
        next: 'Open the company in TallyPrime on the connected computer.',
    },
    update_failing: {
        headline: 'Updates from TallyPrime are not going through',
        next: 'Receivables data says what went wrong and what to do.',
    },
    needs_approval: {
        headline: 'TallyPrime updates are paused until the connection is approved again',
        next: 'The person who approved it can no longer sync. An owner or admin approves it again in Receivables data.',
    },
    held_for_review: {
        headline: 'An update from TallyPrime is waiting for review',
        next: 'It would mark many accounts as paid at once, so it is held until somebody looks at it in Receivables data.',
    },
    disconnected: {
        headline: 'TallyPrime is disconnected',
        next: 'Connect TallyPrime or a Google Sheet again in Receivables data.',
    },
    sheet_sync_failed: {
        headline: 'The last sync from the Google Sheet failed',
        next: 'Sync again from Receivables data; it says what went wrong.',
    },
    sheet_unreachable: {
        headline: 'Timely cannot read the Google Sheet',
        next: 'Check in Receivables data that the sheet still exists and is shared with Timely.',
    },
    sheet_out_of_date: {
        headline: 'The Google Sheet has not been synced for over a week',
        next: 'Sync it from Receivables data.',
    },
    no_source: {
        headline: 'No receivables source is connected',
        next: 'Connect TallyPrime or a Google Sheet in Receivables data.',
    },
};

/** These leave the figures as they are for good, not merely late. */
const NOT_UPDATING: ReadonlySet<FreshnessProblem> = new Set(['disconnected', 'no_source']);

const span = (minutes: number): string => {
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    const hours = Math.floor(minutes / 60);
    if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'}`;
    const days = Math.floor(hours / 24);
    return `${days} days`;
};

export function freshnessMessage(reading: FreshnessReading, audience: FreshnessAudience, fmt: AgeFormat): FreshnessMessage | null {
    const { freshness: f, unavailable, offline, now } = reading;
    if (!f) {
        if (!unavailable && !offline) return null; // still asking
        const headline = offline ? 'You are offline' : 'Could not check when the receivables were last updated';
        return {
            tone: 'notice', headline,
            detail: offline
                ? 'The figures are as they were when this page last loaded.'
                : 'Until it can be checked, treat the figures as possibly out of date.',
            next: null, linkToSource: false, announce: headline,
        };
    }
    if (f.level === 'none' && !f.problem) return null; // nothing connected, nothing shown as figures

    const age = describeAge(f.updatedAt, now, fmt);
    const src = f.source ? SOURCE[f.source] : 'its source';
    const showing = age ? `Showing receivables from the last successful update from ${src}, ${age}.` : 'No figures have arrived from it yet.';
    const admin = audience !== 'member';
    const link = audience !== 'member';
    const recheck = unavailable ? ' Could not check again just now.' : offline ? ' You are offline.' : '';

    // Something must be done: said to those who can do it, with the next step.
    if (admin && f.problem) {
        const p = ADMIN_PROBLEM[f.problem];
        const silent = f.problem === 'connector_silent' && f.checkedInAt
            ? `${p.headline} for ${span(Math.max(0, Math.floor((now.getTime() - new Date(f.checkedInAt).getTime()) / 60_000)))}`
            : f.problem === 'connector_silent' ? `${p.headline} yet` : p.headline;
        const detail = f.problem === 'no_source'
            ? (age ? `These figures were last updated ${age} and will not change until a source is connected.` : null)
            : f.problem === 'disconnected'
                ? (age ? `Showing receivables from its last update, ${age}. They will not change until a source is connected.` : null)
                : showing;
        return {
            tone: 'action', headline: silent, detail: detail && detail + recheck,
            next: audience === 'manage' ? p.next : `${p.next} Whoever manages the data source can do this.`,
            linkToSource: link, announce: `Receivables need attention: ${p.headline}.`,
        };
    }

    // Nothing will update these figures any more: everybody is told, plainly.
    if (f.problem && NOT_UPDATING.has(f.problem)) {
        return {
            tone: 'warning', headline: 'These receivables are not being updated',
            detail: `${age ? `Last updated ${age}. ` : ''}Check recent payments before contacting a customer.${recheck}`,
            next: null, linkToSource: false, announce: 'These receivables are not being updated.',
        };
    }

    if (f.level === 'setup') {
        const headline = f.source === 'google_sheets' ? 'The Google Sheet has not been synced yet' : 'TallyPrime is being set up';
        return {
            tone: 'notice', headline,
            detail: `Receivables start updating with the first update from ${src}.`,
            next: null, linkToSource: link, announce: headline,
        };
    }

    if (f.level === 'stale') {
        return admin ? {
            tone: 'warning', headline: `Receivables have not updated for more than ${f.source === 'google_sheets' ? 'a week' : '6 hours'}`,
            detail: showing + recheck,
            next: f.source === 'tally' ? 'Timely Connector is checking in, but no update has arrived. Receivables data has the detail.' : null,
            linkToSource: link, announce: 'Receivables may be out of date.',
        } : {
            tone: 'warning', headline: `These receivables were last updated ${age}`,
            detail: `They may be out of date. Check recent payments before contacting a customer.${recheck}`,
            next: null, linkToSource: false, announce: 'These receivables may be out of date.',
        };
    }

    if (f.level === 'delayed') {
        const usual = f.source === 'tally' ? ' (usually within the hour)' : '';
        return {
            tone: 'notice', headline: `Receivables last updated ${age}${usual}`,
            detail: `Payments received since then may not show yet.${recheck}`,
            next: null, linkToSource: link && f.source === 'google_sheets', announce: 'Receivables have not been updated recently.',
        };
    }

    const quiet = [`Updated from ${src} ${age}`];
    if (f.syncing) quiet.push('updating now');
    if (unavailable) quiet.push('could not check again just now');
    else if (offline) quiet.push('you are offline');
    return { tone: 'quiet', headline: quiet.join(' · '), detail: null, next: null, linkToSource: false, announce: null };
}
